/**
 * Transform functions for Vision benefits
 *
 * These functions handle the bi-directional transformation between:
 * - Frontend grid format (flat fields)
 * - Backend API format (nested objects within networkBenefits array)
 *
 * Note: Vision uses networkBenefits array structure but with networkDependent: false
 * on all fields, meaning we only edit one set of values (stored as inNetwork).
 */

import {
  cleanupNotDisclosedBenefits,
  transformTypeAmountFromApi,
  transformTypeAmountToApi,
  transformTypeMultipleAmountsFromApi,
  transformTypeMultipleAmountsToApi,
} from "../shared/builders";

import type {
  VisionBenefitsApi,
  VisionBenefitsGrid,
  VisionExtendedBenefitApi,
  VisionNetworkBenefitApi,
} from "./types";

/**
 * Transforms Vision benefits from frontend grid format to API format
 *
 * Converts flat grid fields into nested API structures with networkBenefits array.
 *
 * @param benefits - The benefits object from the frontend grid (flat fields)
 * @returns Transformed benefits object matching the API structure (nested objects)
 */
export const transformVisionBenefitsToApi = (
  benefits: VisionBenefitsGrid,
): VisionBenefitsApi => {
  const transformed: VisionBenefitsApi = { objectType: "vision" };

  // Build the network benefit object
  const networkBenefit: VisionNetworkBenefitApi = {};

  // Network Name
  if (benefits.networkName !== undefined) {
    networkBenefit.networkName = benefits.networkName;
  }

  // Exam Copay - type + amount
  const examCopay = transformTypeAmountToApi(
    benefits.examCopayType,
    benefits.examCopayAmount,
  );
  if (examCopay) {
    networkBenefit.examCopay =
      examCopay as VisionNetworkBenefitApi["examCopay"];
  }

  // Materials Copay - type + amount
  const materialsCopay = transformTypeAmountToApi(
    benefits.materialsCopayType,
    benefits.materialsCopayAmount,
  );
  if (materialsCopay) {
    networkBenefit.materialsCopay =
      materialsCopay as VisionNetworkBenefitApi["materialsCopay"];
  }

  // Single Lenses - type + copayAmount + allowanceAmount
  const singleLenses = transformTypeMultipleAmountsToApi(
    benefits.singleLensesType,
    {
      copayAmount: benefits.singleLensesCopayAmount,
      allowanceAmount: benefits.singleLensesAllowanceAmount,
    },
  );
  if (singleLenses) {
    networkBenefit.singleLenses =
      singleLenses as VisionNetworkBenefitApi["singleLenses"];
  }

  // Bifocal Lenses - type + copayAmount + allowanceAmount
  const bifocalLenses = transformTypeMultipleAmountsToApi(
    benefits.bifocalLensesType,
    {
      copayAmount: benefits.bifocalLensesCopayAmount,
      allowanceAmount: benefits.bifocalLensesAllowanceAmount,
    },
  );
  if (bifocalLenses) {
    networkBenefit.bifocalLenses =
      bifocalLenses as VisionNetworkBenefitApi["bifocalLenses"];
  }

  // Trifocal Lenses - type + copayAmount + allowanceAmount
  const trifocalLenses = transformTypeMultipleAmountsToApi(
    benefits.trifocalLensesType,
    {
      copayAmount: benefits.trifocalLensesCopayAmount,
      allowanceAmount: benefits.trifocalLensesAllowanceAmount,
    },
  );
  if (trifocalLenses) {
    networkBenefit.trifocalLenses =
      trifocalLenses as VisionNetworkBenefitApi["trifocalLenses"];
  }

  // Lenticular Lenses - type + copayAmount + allowanceAmount
  const lenticularLenses = transformTypeMultipleAmountsToApi(
    benefits.lenticularLensesType,
    {
      copayAmount: benefits.lenticularLensesCopayAmount,
      allowanceAmount: benefits.lenticularLensesAllowanceAmount,
    },
  );
  if (lenticularLenses) {
    networkBenefit.lenticularLenses =
      lenticularLenses as VisionNetworkBenefitApi["lenticularLenses"];
  }

  // Standard Progressive - type + copayAmount + allowanceAmount
  const standardProgressive = transformTypeMultipleAmountsToApi(
    benefits.standardProgressiveType,
    {
      copayAmount: benefits.standardProgressiveCopayAmount,
      allowanceAmount: benefits.standardProgressiveAllowanceAmount,
    },
  );
  if (standardProgressive) {
    networkBenefit.standardProgressive =
      standardProgressive as VisionNetworkBenefitApi["standardProgressive"];
  }

  // Standard Frames - type + copayAmount + allowanceAmount
  const standardFrames = transformTypeMultipleAmountsToApi(
    benefits.standardFramesType,
    {
      copayAmount: benefits.standardFramesCopayAmount,
      allowanceAmount: benefits.standardFramesAllowanceAmount,
    },
  );
  if (standardFrames) {
    networkBenefit.standardFrames =
      standardFrames as VisionNetworkBenefitApi["standardFrames"];
  }

  // Additional Discounts on Frames - type + amount
  const additionalDiscountsOnFrames = transformTypeAmountToApi(
    benefits.additionalDiscountsOnFramesType,
    benefits.additionalDiscountsOnFramesAmount,
    ["upToPercentageOff", "upToDollarAmountOff"],
  );
  if (additionalDiscountsOnFrames) {
    networkBenefit.additionalDiscountsOnFrames =
      additionalDiscountsOnFrames as VisionNetworkBenefitApi["additionalDiscountsOnFrames"];
  }

  // Contact Lens Elective - type + copayAmount + allowanceAmount
  const contactLensElective = transformTypeMultipleAmountsToApi(
    benefits.contactLensElectiveType,
    {
      copayAmount: benefits.contactLensElectiveCopayAmount,
      allowanceAmount: benefits.contactLensElectiveAllowanceAmount,
    },
  );
  if (contactLensElective) {
    networkBenefit.contactLensElective =
      contactLensElective as VisionNetworkBenefitApi["contactLensElective"];
  }

  // Contact Lens Exam - type + copayAmount + allowanceAmount
  const contactLensExam = transformTypeMultipleAmountsToApi(
    benefits.contactLensExamType,
    {
      copayAmount: benefits.contactLensExamCopayAmount,
      allowanceAmount: benefits.contactLensExamAllowanceAmount,
    },
  );
  if (contactLensExam) {
    networkBenefit.contactLensExam =
      contactLensExam as VisionNetworkBenefitApi["contactLensExam"];
  }

  // Add the benefit to the networkBenefits array
  transformed.networkBenefits = [networkBenefit];

  // Root-level frequency fields
  if (benefits.examFrequency) {
    transformed.examFrequency = benefits.examFrequency;
  }
  if (benefits.lensesFrequency) {
    transformed.lensesFrequency = benefits.lensesFrequency;
  }
  if (benefits.framesFrequency) {
    transformed.framesFrequency = benefits.framesFrequency;
  }

  // Administrative fields
  if (benefits.annualOpenEnrollment) {
    transformed.annualOpenEnrollment = benefits.annualOpenEnrollment;
  }
  if (benefits.employerContribution) {
    transformed.employerContribution = benefits.employerContribution;
  }

  // Extended (non-v5) fields — CD-1661/CD-1983. Sent as a full object
  // (a full-replace PATCH, like the rest of `benefits`) whenever at least one
  // extended field is set, so an edit to any other field on the plan doesn't
  // drop the extended values already on it. Omitted entirely when every
  // extended field is unset, so an edit that never touches these fields
  // doesn't create an empty extended row on the backend.
  const extended = {
    premiumProgressiveType: benefits.premiumProgressiveType,
    premiumProgressiveCopayAmount: benefits.premiumProgressiveCopayAmount,
    premiumProgressiveAllowanceAmount:
      benefits.premiumProgressiveAllowanceAmount,
    conventionalContactType: benefits.conventionalContactType,
    conventionalContactAdditionalDiscountAmount:
      benefits.conventionalContactAdditionalDiscountAmount,
    conventionalContactAllowanceAmount:
      benefits.conventionalContactAllowanceAmount,
    disposableContactType: benefits.disposableContactType,
    disposableContactCopayAmount: benefits.disposableContactCopayAmount,
    disposableContactAllowanceAmount: benefits.disposableContactAllowanceAmount,
    contactLensMedNecType: benefits.contactLensMedNecType,
    contactLensMedNecCopayAmount: benefits.contactLensMedNecCopayAmount,
    contactLensMedNecAllowanceAmount: benefits.contactLensMedNecAllowanceAmount,
    progressiveLensesType: benefits.progressiveLensesType,
    progressiveLensesCopayAmount: benefits.progressiveLensesCopayAmount,
    progressiveLensesAllowanceAmount: benefits.progressiveLensesAllowanceAmount,
    eligibilityHours: benefits.eligibilityHours,
  };
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    transformed.extended = extended;
  }

  return cleanupNotDisclosedBenefits(transformed);
};

/**
 * Transforms Vision benefits from API format to frontend grid format
 *
 * Converts nested API structures with networkBenefits array into flat grid fields.
 *
 * @param benefits - The benefits object from the API (nested objects)
 * @returns Transformed benefits object matching the grid structure (flat fields)
 */
export const transformVisionBenefitsFromApi = (
  benefits: VisionBenefitsApi,
): VisionBenefitsGrid => {
  if (!benefits) {
    return { objectType: "vision" };
  }

  const transformed: VisionBenefitsGrid = { objectType: "vision" };

  // Extract the first (and only) benefit from networkBenefits array
  // Vision doesn't use multiple networks, so we just take the first item
  const networkBenefit: VisionNetworkBenefitApi | undefined =
    benefits.networkBenefits?.[0];

  // Network Name
  transformed.networkName = networkBenefit?.networkName;

  // Exam Copay - type + amount
  Object.assign(
    transformed,
    transformTypeAmountFromApi(networkBenefit?.examCopay, "examCopay"),
  );

  // Materials Copay - type + amount
  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      networkBenefit?.materialsCopay,
      "materialsCopay",
    ),
  );

  // Single Lenses - type + copayAmount + allowanceAmount
  Object.assign(
    transformed,
    transformTypeMultipleAmountsFromApi(
      networkBenefit?.singleLenses,
      "singleLenses",
      ["copayAmount", "allowanceAmount"],
    ),
  );

  // Bifocal Lenses - type + copayAmount + allowanceAmount
  Object.assign(
    transformed,
    transformTypeMultipleAmountsFromApi(
      networkBenefit?.bifocalLenses,
      "bifocalLenses",
      ["copayAmount", "allowanceAmount"],
    ),
  );

  // Trifocal Lenses - type + copayAmount + allowanceAmount
  Object.assign(
    transformed,
    transformTypeMultipleAmountsFromApi(
      networkBenefit?.trifocalLenses,
      "trifocalLenses",
      ["copayAmount", "allowanceAmount"],
    ),
  );

  // Lenticular Lenses - type + copayAmount + allowanceAmount
  Object.assign(
    transformed,
    transformTypeMultipleAmountsFromApi(
      networkBenefit?.lenticularLenses,
      "lenticularLenses",
      ["copayAmount", "allowanceAmount"],
    ),
  );

  // Standard Progressive - type + copayAmount + allowanceAmount
  Object.assign(
    transformed,
    transformTypeMultipleAmountsFromApi(
      networkBenefit?.standardProgressive,
      "standardProgressive",
      ["copayAmount", "allowanceAmount"],
    ),
  );

  // Standard Frames - type + copayAmount + allowanceAmount
  Object.assign(
    transformed,
    transformTypeMultipleAmountsFromApi(
      networkBenefit?.standardFrames,
      "standardFrames",
      ["copayAmount", "allowanceAmount"],
    ),
  );

  // Additional Discounts on Frames - type + amount
  Object.assign(
    transformed,
    transformTypeAmountFromApi(
      networkBenefit?.additionalDiscountsOnFrames,
      "additionalDiscountsOnFrames",
    ),
  );

  // Contact Lens Elective - type + copayAmount + allowanceAmount
  Object.assign(
    transformed,
    transformTypeMultipleAmountsFromApi(
      networkBenefit?.contactLensElective,
      "contactLensElective",
      ["copayAmount", "allowanceAmount"],
    ),
  );

  // Contact Lens Exam - type + copayAmount + allowanceAmount
  Object.assign(
    transformed,
    transformTypeMultipleAmountsFromApi(
      networkBenefit?.contactLensExam,
      "contactLensExam",
      ["copayAmount", "allowanceAmount"],
    ),
  );

  // Root-level frequency fields
  transformed.examFrequency = benefits.examFrequency;
  transformed.lensesFrequency = benefits.lensesFrequency;
  transformed.framesFrequency = benefits.framesFrequency;

  // Administrative fields
  transformed.annualOpenEnrollment = benefits.annualOpenEnrollment;
  transformed.employerContribution = benefits.employerContribution;

  // Extended (non-v5) fields — CD-1661/CD-1983
  Object.assign(transformed, transformVisionExtendedFromApi(benefits.extended));

  return transformed;
};

type VisionExtendedGridFields = Pick<
  VisionBenefitsGrid,
  | "premiumProgressiveType"
  | "premiumProgressiveCopayAmount"
  | "premiumProgressiveAllowanceAmount"
  | "conventionalContactType"
  | "conventionalContactAdditionalDiscountAmount"
  | "conventionalContactAllowanceAmount"
  | "disposableContactType"
  | "disposableContactCopayAmount"
  | "disposableContactAllowanceAmount"
  | "contactLensMedNecType"
  | "contactLensMedNecCopayAmount"
  | "contactLensMedNecAllowanceAmount"
  | "progressiveLensesType"
  | "progressiveLensesCopayAmount"
  | "progressiveLensesAllowanceAmount"
  | "eligibilityHours"
>;

const VISION_EXTENDED_FIELD_KEYS: (keyof VisionExtendedGridFields)[] = [
  "premiumProgressiveType",
  "premiumProgressiveCopayAmount",
  "premiumProgressiveAllowanceAmount",
  "conventionalContactType",
  "conventionalContactAdditionalDiscountAmount",
  "conventionalContactAllowanceAmount",
  "disposableContactType",
  "disposableContactCopayAmount",
  "disposableContactAllowanceAmount",
  "contactLensMedNecType",
  "contactLensMedNecCopayAmount",
  "contactLensMedNecAllowanceAmount",
  "progressiveLensesType",
  "progressiveLensesCopayAmount",
  "progressiveLensesAllowanceAmount",
  "eligibilityHours",
];

// Extended (non-v5) fields — CD-1661/CD-1983. Split out of
// transformVisionBenefitsFromApi to keep that function's complexity down.
// Grid and API field names match 1:1, so a single loop over the shared key
// list replaces one `?? undefined` assignment per field. Every key is
// present (as undefined) even when `extended` itself is absent, so a later
// edit to any other field doesn't leave a stale extended value in the grid
// row from a previous plan.
const transformVisionExtendedFromApi = (
  extended: VisionExtendedBenefitApi | undefined,
): VisionExtendedGridFields =>
  Object.fromEntries(
    VISION_EXTENDED_FIELD_KEYS.map((key) => [
      key,
      extended?.[key] ?? undefined,
    ]),
  ) as VisionExtendedGridFields;

/** Grid keys that persist under `benefits.extended` — see STD_EXTENDED_GRID_FIELD_KEYS. */
export const VISION_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> = new Set(
  VISION_EXTENDED_FIELD_KEYS,
);
